import { get, each, isNaN, isUndefined, groupBy, sum, isEmpty } from 'lodash';
import {
  QUESTION_MATRIX_OPEN_ENDED_STYLES,
  QUESTION_MATRIX_OPEN_ENDED_DECIMALS_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_MIN_VALUE_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_MAX_VALUE_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_SCROLL_ANIMATION_TIME_HORIZONTAL,
  QUESTION_MATRIX_OPEN_ENDED_SCROLL_ANIMATION_TIME_VERTICAL,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TOTAL_VALUE_DEFAULT,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TYPES,
} from './constants';
import {
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
  MAX_TEXT_LENGTH,
  MIN_TEXT_LENGTH,
} from '../../../../constants';
import { roundNumberToStringWithDecimals } from '../../../../utils';

function highlightAndScrollToFieldListFirstItem({
  list,
  questionWrapper,
  containerSelector,
  containerClassRequired,
  animationTime,
  validationMessage,
  highlight = true,
}) {
  if (highlight) {
    // highlight with red border
    each(list, ({ domElement }) => {
      $(domElement)
        .parents(containerSelector)
        .addClass(containerClassRequired);
    });
  }

  // show validation prompt for a couple of seconds
  Pollfish.ui.showValidationMessage(questionWrapper, validationMessage);

  // scroll to first element
  const invalidAnswerElement = list.shift().domElement;
  Pollfish.ui.scrollToElementPosition(invalidAnswerElement, animationTime);
  Pollfish.ui.matrixOpenEndedAnswerSlideDown(invalidAnswerElement);
}

export default function isValid(question) {
  if (
    ![
      QUESTION_MATRIX_OPEN_ENDED,
      QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
      QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
    ].includes(question.style)
  ) {
    return true;
  }
  const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);

  const options = get(question, 'matrix.validation.openEndedValidation', {});
  const openEndedConfig = get(question, 'matrix.openEndedConfig', {});
  const openEndedStyle = openEndedConfig.style;
  const min = !isUndefined(options.min)
    ? options.min
    : QUESTION_MATRIX_OPEN_ENDED_MIN_VALUE_DEFAULT;
  const max = !isUndefined(options.max)
    ? options.max
    : QUESTION_MATRIX_OPEN_ENDED_MAX_VALUE_DEFAULT;
  const total = !isUndefined(options.total)
    ? options.total
    : QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TOTAL_VALUE_DEFAULT;
  const constantSumType =
    openEndedConfig.constantSumType ||
    QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TYPES.MUST_TOTAL;
  const allowedDecimals =
    options.decimals || QUESTION_MATRIX_OPEN_ENDED_DECIMALS_DEFAULT;
  const allowedMaxLength = options.maxLength || MAX_TEXT_LENGTH;
  const allowedMinLength = options.minLength || MIN_TEXT_LENGTH;
  const answerIds = [];
  const emptyAnswers = [];
  const emptyRequiredAnswers = [];
  let animationTime = QUESTION_MATRIX_OPEN_ENDED_SCROLL_ANIMATION_TIME_HORIZONTAL;
  const isValidAnswerText = (text) => {
    if (
      [
        QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
        QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
      ].includes(question.style)
    ) {
      return !isNaN(parseFloat(text));
    }

    return text.trim().length >= MIN_TEXT_LENGTH;
  };

  if (openEndedStyle === QUESTION_MATRIX_OPEN_ENDED_STYLES.VERTICAL) {
    animationTime = QUESTION_MATRIX_OPEN_ENDED_SCROLL_ANIMATION_TIME_VERTICAL;
  }

  // iterate over all answers and get typed values
  const answerInputs = $(questionWrapper).find(
    '.answer--matrix-open-ended-answer-input',
  );
  const baseClass = 'answer--matrix-open-ended-answer-input-container';
  const containerSelector = `.${baseClass}`;
  const containerClassRequired = `${baseClass}-required`;
  const message =
    question.style === QUESTION_MATRIX_OPEN_ENDED
      ? 'warnings.open_ended'
      : 'warnings.matrixNumericOpenEnded';
  const payloadHighLight = {
    questionWrapper,
    containerSelector,
    containerClassRequired,
    animationTime,
    validationMessage: Pollfish.ui.getText(message),
  };

  each(answerInputs, (domElement) => {
    const answerText = $(domElement).val();
    const answerRow = $(domElement).attr('data-row-id');
    const isAnswerValid = isValidAnswerText(answerText);

    if (isAnswerValid) {
      answerIds.push({ text: answerText, rowId: answerRow, domElement });
    } else {
      emptyAnswers.push({ text: answerText, rowId: answerRow, domElement });
    }

    const isRequired = $(domElement).attr('data-input-required') === 'true';
    if (isRequired && !isAnswerValid) {
      emptyRequiredAnswers.push({
        text: answerText,
        rowId: answerRow,
        domElement,
      });
    }
  });

  if (emptyRequiredAnswers.length !== 0) {
    highlightAndScrollToFieldListFirstItem({
      ...payloadHighLight,
      list: emptyRequiredAnswers,
    });

    return false;
  }

  // at least one field should have been typed
  if (answerIds.length === 0) {
    highlightAndScrollToFieldListFirstItem({
      ...payloadHighLight,
      list: emptyAnswers,
      highlight: false,
    });

    return false;
  }

  if (question.style === QUESTION_MATRIX_OPEN_ENDED) {
    // not required answers should have at least some min chars if are filled
    const notAllowed = emptyAnswers.filter(
      ({ text }) => text.length > 0 && text.trim().length < MIN_TEXT_LENGTH,
    );

    if (notAllowed.length > 0) {
      highlightAndScrollToFieldListFirstItem({
        ...payloadHighLight,
        validationMessage: Pollfish.ui.getText('warnings.open_ended'),
        list: notAllowed,
      });

      return false;
    }

    const lengthNotBetweenMinMax = answerIds.filter(
      ({ text }) =>
        text.length < allowedMinLength || text.length > allowedMaxLength,
    );
    if (lengthNotBetweenMinMax.length > 0) {
      const updatedText = Pollfish.ui
        .getText('warnings.openEndedMinLimit')
        .replace('XXX', allowedMinLength);

      highlightAndScrollToFieldListFirstItem({
        ...payloadHighLight,
        list: lengthNotBetweenMinMax,
        validationMessage: updatedText,
      });

      return false;
    }

    return true;
  }

  const valueNotBetweenMinMax = answerIds.filter(
    ({ text }) => parseFloat(text) < min || parseFloat(text) > max,
  );

  if (valueNotBetweenMinMax.length > 0) {
    const minWithDecimals = roundNumberToStringWithDecimals(
      min,
      allowedDecimals,
    );
    const maxWithDecimals = roundNumberToStringWithDecimals(
      max,
      allowedDecimals,
    );
    // indicate all fields where the value is not between min and max value with a red border
    each($(valueNotBetweenMinMax), ({ domElement }) => {
      $(domElement)
        .parents(containerSelector)
        .addClass(containerClassRequired);
    });
    // show prompt for a couple of seconds for minimum and maximum accepted value
    Pollfish.ui.showValidationMessage(
      questionWrapper,
      Pollfish.ui.getText('warnings.matrixNumericOpenEndedRange', {
        min: minWithDecimals,
        max: maxWithDecimals,
      }),
    );
    const invalidAnswerElement = valueNotBetweenMinMax.shift().domElement;
    Pollfish.ui.scrollToElementPosition(invalidAnswerElement, animationTime);
    Pollfish.ui.matrixOpenEndedAnswerSlideDown(invalidAnswerElement);
    return false;
  }

  if (question.style !== QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM) {
    return true;
  }

  const valueWithNegativeNumber = answerIds.filter(
    ({ text }) => parseFloat(text) < 0,
  );

  if (valueWithNegativeNumber.length > 0) {
    each($(valueWithNegativeNumber), ({ domElement }) => {
      $(domElement)
        .parents(containerSelector)
        .addClass(containerClassRequired);
    });
    const text =
      Pollfish.ui.getText('matrixVanWestendorp.warnings.min', '0') || '';
    Pollfish.ui.showValidationMessage(questionWrapper, text);
    const invalidAnswerElement = valueWithNegativeNumber.shift().domElement;
    Pollfish.ui.scrollToElementPosition(invalidAnswerElement, animationTime);
    Pollfish.ui.matrixOpenEndedAnswerSlideDown(invalidAnswerElement);
    return false;
  }

  // Here we continue the validations for Constant Sum
  const groupedByRow = groupBy(answerIds, 'rowId');
  const elementWithError = Object.values(groupedByRow).find((group) => {
    const groupRowValues = group.map(({ text }) => parseFloat(text));
    if (
      constantSumType ===
        QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM_TYPES.MUST_TOTAL &&
      sum(groupRowValues) !== total
    ) {
      return group;
    }
    return false;
  });

  if (elementWithError) {
    $('.answer--matrix-open-ended-info-message').addClass(
      'answer--matrix-open-ended-info-message-invalid',
    );
    const text =
      Pollfish.ui.getText('warnings.matrixConstantSumOpenEndedMustTotal') || '';
    Pollfish.ui.showValidationMessage(
      questionWrapper,
      text.replace('XXXX', total),
    );
    const invalidAnswerElement = elementWithError.shift().domElement;
    Pollfish.ui.scrollToElementPosition(invalidAnswerElement, animationTime);
    Pollfish.ui.matrixOpenEndedAnswerSlideDown(invalidAnswerElement);
    return false;
  }

  return true;
}

export function isRowValuesFilled(rowElement, element = 'input') {
  const inputElements = rowElement.querySelectorAll(element);
  let hasAnyEmptyValue = false;
  inputElements.forEach((input) => {
    if (hasAnyEmptyValue) {
      return;
    }
    hasAnyEmptyValue = isEmpty(input.value);
  });
  return !hasAnyEmptyValue;
}
